import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import Swal from "sweetalert2";

import useAuth from "../../hooks/useAuth";
import Pagination from "../../components/Table/pagination";
import { ReactComponent as Edit } from "../../assets/images/svg/Edit.svg";
import { ReactComponent as Delete } from "../../assets/images/svg/Delete.svg";
import { set } from "date-fns";
import useFetch from "../../hooks/useFetch";

const API_URL = process.env.REACT_APP_API_URL;

const CampaignCustomers = () => {
  const fetchData = useFetch();
  const { auth } = useAuth();

  const initialPagination = {
    next: "",
    previous: "",
    count: 0,
    total_pages: 0,
    current_page: 1,
    page_size: 5,
    current_page_count: 0,
  };

  const [customerList, setCustomerList] = useState([]);
  const [pagination, setPagination] =
    useState(initialPagination);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState(null);

  const [customerName, setCustomerName] = useState("");
  const [customerPhoneNumber, setCustomerPhoneNumber] =
    useState("");

  const [status, setStatus] = useState("");
  const [searchText, setSearchText] = useState("");

  const [campaignList, setCampaignList] = useState([]);
  const [campaign, setCampaign] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [showCampaignMenu, setShowCampaignMenu] =
    useState(false);

  const [sortBy, setSortBy] = useState("by_name");
  const [dncValue, setDncValue] = useState("");

  const [selectionMode, setSelectionMode] = useState("");
  const [selectedCustomersMap, setSelectedCustomersMap] =
    useState({});
  const [customerDnc, setCustomerDnc] = useState(false);

  const selectedCustomers = useMemo(
    () => Object.values(selectedCustomersMap),
    [selectedCustomersMap]
  );

  const filteredCampaigns = useMemo(() => {
    const searchValue = campaignSearch.trim().toLowerCase();
    if (!searchValue) {
      return campaignList;
    }
    return campaignList.filter((item) => item?.name?.toLowerCase().includes(searchValue));
  }, [campaignList, campaignSearch]);

  const selectableCustomers = useMemo(() => {
    if (selectionMode === "delete") {
      return customerList.filter((customer) => customer.callStatus?.toLowerCase() !== "called");
    }
    if (
      selectionMode === "updateDNC" &&
      selectedCustomers.length > 0
    ) {
      const requiredDnc = Boolean(
        selectedCustomers[0].dnc
      );
      return customerList.filter(
        (customer) =>
          Boolean(customer.dnc) === requiredDnc
      );
    }

    return customerList;
  }, [
    customerList,
    selectionMode,
    selectedCustomers,
  ]);

  /*
   * Header checkbox represents only the current page.
   * Selections on other pages remain unchanged.
   */
  const isSelectAllChecked =
    selectableCustomers.length > 0 &&
    selectableCustomers.every(
      (customer) =>
        Boolean(selectedCustomersMap[customer.id])
    );

  const clearSelection = useCallback(() => {
    setSelectedCustomersMap({});
    setSelectionMode("");
  }, []);

  /*
   * Load customer data.
   *
   * Important:
   * selectedCustomersMap is not reset here.
   * Therefore, selections stay active across pagination.
   */
  const fetchCampaignCustomers = async (
    pageNumber = 1
  ) => {
    if (!auth?.user_details?.id) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/contactcentre/campaign-customers/`,
        {
          params: {
            user_id: auth.user_details.id,
            page: pageNumber,
            campaign: campaign || "",
            search_text: searchText.trim(),
            call_status: status || "",
            sortby: sortBy,
            dnc: dncValue
          },
        }
      );

      const data = response.data;

      setCustomerList(data?.results ?? []);

      setPagination({
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        count: data?.count ?? 0,
        total_pages: data?.total_pages ?? 0,
        current_page:
          data?.current_page ?? pageNumber,
        page_size: data?.page_size ?? 5,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load customers",
        text:
          error?.response?.data?.message ||
          "Please try again after some time.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignList = useCallback(async () => {
    if (!auth?.user_details) {
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/contactcentre/all-campaigns/`,
        {
          params: {
            user_id: auth.user_details.user,
            user_type: auth.user_details.type,
          },
        }
      );

      const data = response.data;

      setCampaignList(
        Array.isArray(data)
          ? data
          : data?.results ?? []
      );
    } catch (error) {
      console.error(
        "Unable to load campaign list:",
        error
      );
    }
  }, [
    auth?.user_details?.type,
    auth?.user_details?.user,
  ]);

  useEffect(() => {
    if (!auth?.user_details?.id) {
      return;
    }

    fetchCampaignCustomers(1);
    fetchCampaignList();

  }, [auth?.user_details?.id, fetchCampaignList]);

  const handleCampaignInputChange = (event) => {
    const value = event.target.value;

    setCampaignSearch(value);
    setShowCampaignMenu(true);

    if (!value.trim()) {
      setCampaign("");
    }
  };

  const handleCampaignSelect = (option) => {
    setCampaign(option.id);
    setCampaignSearch(option.name);
    setShowCampaignMenu(false);
  };

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.customer_name ?? "");
    setCustomerPhoneNumber(customer.customer_phone_number ?? "");
    setCustomerDnc(customer.dnc ?? false);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
    setCustomerName("");
    setCustomerPhoneNumber("");
    setCustomerDnc(false);
  };

  const saveCustomer = async () => {
    if (!selectedCustomer?.id) {
      return;
    }

    if (
      !customerName.trim() ||
      !customerPhoneNumber.trim()
    ) {
      Swal.fire({
        icon: "warning",
        title: "Required fields missing",
        text: "Please enter the customer name and phone number.",
      });

      return;
    }

    setSaving(true);

    try {
      const res_data = await fetchData(
        "post",
        {
          customer_id: selectedCustomer.id,
          customer_name: customerName.trim(),
          customer_phone_number: customerPhoneNumber.trim(),
          dnc: customerDnc,
        },
        `${API_URL}/contactcentre/campaign-customers/`
      );

      // await axios.post(
      //   `${API_URL}/contactcentre/campaign-customers/`,
      //   {
      //     customer_id: selectedCustomer.id,
      //     customer_name: customerName.trim(),
      //     customer_phone_number: customerPhoneNumber.trim(),
      //     dnc: customerDnc,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Bearer ${auth?.accessToken}`,
      //     },
      //   }
      // );

      await Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Customer details have been updated.",
      });

      closeEditModal();

      await fetchCampaignCustomers(
        pagination.current_page
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text:
          error?.response?.data?.message ||
          "Unable to update the customer.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomers = async (customers) => {
    if (!Array.isArray(customers) || !customers.length) {
      Swal.fire({
        icon: "warning",
        title: "No customers selected",
        text: "Please select at least one customer.",
      });
      return;
    }

    const invalidCustomers = customers.filter(
      (customer) => customer.callStatus?.toLowerCase() === "called"
    );

    if (invalidCustomers.length > 0) {
      Swal.fire({
        icon: "error",
        title: "Cannot delete",
        text: "Called customers cannot be deleted.",
      });
      return;
    }

    let customersToDelete = [...customers];

    const customerRows = customers
      .map(
        (customer) => `
          <tr>
            <td style="padding:8px;border:1px solid #ddd;white-space:nowrap;">
              ${customer.customer_name || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;white-space:nowrap;">
              ${customer.customer_phone_number || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;white-space:nowrap;">
              ${customer.campaign_name || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;white-space:nowrap;">
              ${customer.assigned_agent || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;white-space:nowrap;">
              ${customer.callStatus || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;text-align:center;">
              <button
                class="remove-customer-btn"
                data-id="${customer.id}"
                style="
                  width:28px;
                  height:28px;
                  background:#ef4444;
                  color:white;
                  border:none;
                  border-radius:50%;
                  cursor:pointer;
                  font-size:12px;
                  font-weight:bold;
                  display:inline-flex;
                  align-items:center;
                  justify-content:center;
                "
              >
                ✕
              </button>
            </td>
          </tr>
        `
      )
      .join("");

    const result = await Swal.fire({
      title: "Delete selected customers?",
      html: `
        <p id="selected-customer-count" style="margin-bottom:12px;">
          ${customers.length} customer(s) will be deleted.
        </p>

        <div style="max-height:300px;overflow:auto;">
          <table
            style="
              width:100%;
              border-collapse:collapse;
              font-size:13px;
            "
          >
            <thead>
              <tr>
                <th style="padding:8px;border:1px solid #ddd;">Customer</th>
                <th style="padding:8px;border:1px solid #ddd;">Phone</th>
                <th style="padding:8px;border:1px solid #ddd;">Campaign</th>
                <th style="padding:8px;border:1px solid #ddd;">Agent</th>
                <th style="padding:8px;border:1px solid #ddd;">Status</th>
                <th style="padding:8px;border:1px solid #ddd;">Action</th>
              </tr>
            </thead>

            <tbody>
              ${customerRows}
            </tbody>
          </table>
        </div>
      `,
      width: "min(900px,95vw)",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Delete",

      didOpen: () => {
        document
          .querySelectorAll(".remove-customer-btn")
          .forEach((button) => {
            button.addEventListener("click", () => {
              const customerId = Number(
                button.getAttribute("data-id")
              );

              customersToDelete = customersToDelete.filter(
                (customer) => customer.id !== customerId
              );

              setSelectedCustomersMap((prev) => {
                const updated = { ...prev };
                delete updated[customerId];
                return updated;
              });

              button.closest("tr")?.remove();

              const countElement = document.getElementById(
                "selected-customer-count"
              );

              if (countElement) {
                countElement.textContent =
                  `${customersToDelete.length} customer(s) will be deleted.`;
              }

              if (customersToDelete.length === 0) {
                Swal.close();
              }
            });
          });
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    if (customersToDelete.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No customers selected",
        text: "Please select at least one customer.",
      });
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/contactcentre/campaign-customers/`,
        {
          headers: {
            "Content-Type": "application/json",
          },
          data: {
            customer_id: customersToDelete.map(
              (customer) => customer.id
            ),
            camp_id: campaign || null,
          },
        }
      );

      const deletedIds = new Set(
        customersToDelete.map(
          (customer) => customer.id
        )
      );

      setCustomerList((previousList) =>
        previousList.filter(
          (customer) => !deletedIds.has(customer.id)
        )
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Selected customers have been deleted.",
      });

      clearSelection();

      if (typeof fetchCampaignCustomers === "function") {
        await fetchCampaignCustomers(
          pagination?.current_page || 1
        );
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error?.response?.data?.message ||
          "Unable to delete the selected customers.",
      });
    }
  };
  
  const updateSelectedDnc = async () => {
    if (!selectedCustomers.length) {
      Swal.fire({
        icon: "warning",
        title: "No customers selected",
        text: "Please select at least one customer.",
      });
      return;
    }

    const firstDncValue = Boolean(selectedCustomers[0].dnc);

    const hasDifferentDncValues = selectedCustomers.some(
      (customer) =>
        Boolean(customer.dnc) !== firstDncValue
    );

    if (hasDifferentDncValues) {
      Swal.fire({
        icon: "warning",
        title: "Different DNC status",
        text: "Please select customers with the same DNC status.",
      });
      return;
    }

    const newDncValue = !firstDncValue;

    let customersToUpdate = [...selectedCustomers];

    const selectedCustomerRows = selectedCustomers
      .map(
        (customer) => `
          <tr>
            <td style="padding:8px;border:1px solid #ddd;">
              ${customer.customer_name || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;">
              ${customer.customer_phone_number || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;">
              ${customer.campaign_name || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;">
              ${customer.assigned_agent || "-"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;">
              ${customer.dnc ? "Yes" : "No"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;">
              ${newDncValue ? "Yes" : "No"}
            </td>

            <td style="padding:8px;border:1px solid #ddd;text-align:center;">
              <button
                class="remove-customer-btn"
                data-id="${customer.id}"
                style="
                  width:28px;
                  height:28px;
                  background:#ef4444;
                  color:white;
                  border:none;
                  border-radius:50%;
                  cursor:pointer;
                  font-size:12px;
                  font-weight:bold;
                  display:inline-flex;
                  align-items:center;
                  justify-content:center;
                "
              >
                ✕
              </button>
            </td>
          </tr>
        `
      )
      .join("");

    const result = await Swal.fire({
      icon: "question",
      title: newDncValue
        ? "Mark selected customers as DNC?"
        : "Remove selected customers from DNC?",

      html: `
        <p id="selected-customer-count" style="margin-bottom:12px;">
          ${selectedCustomers.length} customer(s) will be updated.
        </p>

        <div style="max-height:320px;overflow:auto;">
          <table
            style="
              width:100%;
              border-collapse:collapse;
              font-size:13px;
              text-align:left;
            "
          >
            <thead>
              <tr>
                <th style="padding:8px;border:1px solid #ddd;">Customer</th>
                <th style="padding:8px;border:1px solid #ddd;">Phone</th>
                <th style="padding:8px;border:1px solid #ddd;">Campaign</th>
                <th style="padding:8px;border:1px solid #ddd;">Agent</th>
                <th style="padding:8px;border:1px solid #ddd;">Current DNC</th>
                <th style="padding:8px;border:1px solid #ddd;">New DNC</th>
                <th style="padding:8px;border:1px solid #ddd;">Action</th>
              </tr>
            </thead>

            <tbody>
              ${selectedCustomerRows}
            </tbody>
          </table>
        </div>
      `,

      width: "min(1000px,95vw)",
      showCancelButton: true,
      confirmButtonText: "Update",
      confirmButtonColor: newDncValue
        ? "#dc2626"
        : "#16a34a",

      didOpen: () => {
        document
          .querySelectorAll(".remove-customer-btn")
          .forEach((button) => {
            button.addEventListener("click", () => {
              const customerId = Number(
                button.getAttribute("data-id")
              );

              customersToUpdate = customersToUpdate.filter(
                (customer) => customer.id !== customerId
              );

              setSelectedCustomersMap((prev) => {
                const updated = { ...prev };
                delete updated[customerId];
                return updated;
              });

              button.closest("tr")?.remove();

              const countElement = document.getElementById(
                "selected-customer-count"
              );

              if (countElement) {
                countElement.textContent = `${customersToUpdate.length} customer(s) will be updated.`;
              }

              if (customersToUpdate.length === 0) {
                Swal.close();
              }
            });
          });
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    if (customersToUpdate.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No customers selected",
        text: "Please select at least one customer.",
      });
      return;
    }

    try {
      const res_data = await fetchData(
        "post",
        {
          customer_ids: customersToUpdate.map(
            (customer) => customer.id
          ),
          dnc: newDncValue,
        },
        `${API_URL}/contactcentre/update-dnc-status/`
      );

      // await axios.post(
      //   `${API_URL}/contactcentre/update-dnc-status/`,
      //   {
      //     customer_ids: customersToUpdate.map(
      //       (customer) => customer.id
      //     ),
      //     dnc: newDncValue,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Bearer ${auth?.accessToken}`,
      //     },
      //   }
      // );

      const updatedIds = new Set(
        customersToUpdate.map(
          (customer) => customer.id
        )
      );

      setCustomerList((previousList) =>
        previousList.map((customer) =>
          updatedIds.has(customer.id)
            ? {
                ...customer,
                dnc: newDncValue,
                is_dnc: newDncValue,
              }
            : customer
        )
      );

      await Swal.fire({
        icon: "success",
        title: "DNC updated",
        text: newDncValue
          ? "Selected customers will not be called again."
          : "Selected customers are enabled for calling.",
      });

      clearSelection();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "DNC update failed",
        text:
          error?.response?.data?.message ||
          "Unable to update the selected customers.",
      });
    }
  };

  const handleSelectRow = (customer) => {
    const isAlreadySelected = Boolean(
      selectedCustomersMap[customer.id]
    );

    /*
     * Unselect the customer.
     */
    if (isAlreadySelected) {
      setSelectedCustomersMap((previous) => {
        const updated = { ...previous };

        delete updated[customer.id];

        return updated;
      });

      return;
    }

    const isCalled =
      customer.callStatus?.toLowerCase() ===
      "called";

    if (selectionMode === "delete" && isCalled) {
      return;
    }

    if (
      selectionMode === "updateDNC" &&
      selectedCustomers.length > 0
    ) {
      const firstSelectedDnc = Boolean(
        selectedCustomers[0].dnc
      );

      const currentDnc = Boolean(customer.dnc);

      if (firstSelectedDnc !== currentDnc) {
        Swal.fire({
          icon: "warning",
          title: "Different DNC status",
          text: "Please select customers with the same DNC status.",
        });

        return;
      }
    }

    setSelectedCustomersMap((previous) => ({
      ...previous,
      [customer.id]: customer,
    }));
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;

    setSelectedCustomersMap((previous) => {
      const updated = { ...previous };

      if (selectionMode !== "updateDNC") {
        selectableCustomers.forEach((customer) => {
          if (checked) {
            updated[customer.id] = customer;
          } else {
            delete updated[customer.id];
          }
        });

        return updated;
      }

      if (!checked) {
        customerList.forEach((customer) => {
          delete updated[customer.id];
        });
        return updated;
      }

      const alreadySelectedCustomers = Object.values(previous);

      // Use the existing selected customer's DNC value.
      // When nothing is selected, use the first current-page customer's DNC value.
      const referenceCustomer =
        alreadySelectedCustomers[0] || customerList[0];

      if (!referenceCustomer) {
        return updated;
      }

      const requiredDncValue = Boolean(referenceCustomer.dnc);

      customerList.forEach((customer) => {
        if (Boolean(customer.dnc) === requiredDncValue) {
          updated[customer.id] = customer;
        }
      });

      return updated;
    });
  };

  const startSelectionMode = (mode) => {
    setSelectedCustomersMap({});
    setSelectionMode(mode);
  };

  const paginationControl = (type, number) => {
    let targetPage = 1;

    if (type === "page") {
      targetPage = Math.min(
        Math.max(Number(number) || 1, 1),
        pagination.total_pages || 1
      );
    } else if (type === "last") {
      targetPage = pagination.total_pages || 1;
    } else if (type === "prev") {
      targetPage = Math.max(
        pagination.current_page - 1,
        1
      );
    } else if (type === "next") {
      targetPage = Math.min(
        pagination.current_page + 1,
        pagination.total_pages || 1
      );
    }

    fetchCampaignCustomers(targetPage);
  };

  return (
    <>
      <h1 className="font-bold text-lg px-3 pt-3 sm:px-4 sm:pt-4">Campaign Customers</h1>
      {/* Filters and action buttons */}
      <div className="p-3 sm:p-4">
        <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-end 2xl:justify-between">
          {/* Filters */}
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:flex 2xl:flex-1 2xl:flex-wrap 2xl:items-end">
            {/* Customer search */}
            <div className="relative w-full 2xl:max-w-[220px]">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  />
                </svg>
              </div>

              <input
                type="text"
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    fetchCampaignCustomers(1);
                  }
                }}
                placeholder="Search customers"
                className="h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {/* Campaign search */}
            <div className="relative w-full 2xl:max-w-[220px]">
              <input
                type="text"
                value={campaignSearch}
                onChange={handleCampaignInputChange}
                onFocus={() =>
                  setShowCampaignMenu(true)
                }
                onBlur={() =>
                  window.setTimeout(
                    () =>
                      setShowCampaignMenu(false),
                    150
                  )
                }
                placeholder="Select campaign"
                className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />

              {showCampaignMenu && (
                <ul className="absolute left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg">
                  {filteredCampaigns.map(
                    (option) => (
                      <li
                        key={option.id}
                        onMouseDown={() =>
                          handleCampaignSelect(
                            option
                          )
                        }
                        className="cursor-pointer px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        {option.name}
                      </li>
                    )
                  )}

                  {filteredCampaigns.length ===
                    0 && (
                    <li className="px-4 py-2 text-sm text-gray-500">
                      No campaigns found
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Status */}
            <select
              value={status}
              onChange={(event) =>
                setStatus(event.target.value)
              }
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 2xl:max-w-[220px]"
            >
              <option value="">
                Call status
              </option>
              <option value="called">Called</option>
              <option value="notcalled">
                Not Called
              </option>
            </select>

            {/* Sorting */}
            <select
              value={sortBy}
              onChange={(event) =>
                setSortBy(event.target.value)
              }
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 2xl:max-w-[220px]"
            >
              <option value="by_name">
                By Name
              </option>
              <option value="by_created">
                By Creation Time
              </option>
            </select>

            <select
              value={dncValue}
              onChange={(event) =>
                setDncValue(event.target.value)
              }
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 2xl:max-w-[220px]"
            >
              <option value="">
                DNC Status
              </option>
              <option value={true}>
                Yes
              </option>
              <option value={false}>
                No
              </option>
            </select>

            {/* Search button */}
            <button
              type="button"
              disabled={loading}
              onClick={() =>
                fetchCampaignCustomers(1)
              }
              className="h-10 w-[100px] rounded-md bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Loading..." : "Search"}
            </button>
          </div>

          {/* Bulk action buttons */}
          <div className="flex w-full flex-wrap items-center justify-start gap-3 2xl:w-auto 2xl:flex-nowrap 2xl:justify-end">
            {selectionMode ? (
              <>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="h-10 rounded-md bg-gray-600 px-5 text-sm font-semibold text-white hover:bg-gray-700"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={
                    selectedCustomers.length === 0
                  }
                  onClick={() => {
                    if (
                      selectionMode === "delete"
                    ) {
                      deleteCustomers(
                        selectedCustomers
                      );
                    } else {
                      updateSelectedDnc();
                    }
                  }}
                  className={`h-10 rounded-md px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                    selectionMode === "delete"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  {selectionMode === "delete" ? `Delete (${selectedCustomers.length})` : `Update DNC (${selectedCustomers.length})`}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() =>
                    startSelectionMode("delete")
                  }
                  className="h-10 rounded-md bg-red-600 px-5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete
                </button>

                <button
                  type="button"
                  onClick={() =>
                    startSelectionMode("updateDNC")
                  }
                  className="h-10 rounded-md bg-amber-600 px-5 text-sm font-semibold text-white hover:bg-amber-700"
                >
                  Update DNC
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Customer table */}
      <div className="relative overflow-x-auto p-4 shadow-md sm:rounded-lg">
        <table className="w-full min-w-[1200px] table-fixed text-sm text-gray-800">
          <thead className="bg-gray-50 text-center text-xs uppercase text-gray-700">
            <tr>
              <th className="w-[7%] px-3 py-3">
                SL-NO
              </th>

              <th className="w-[13%] px-3 py-3">
                Customer
              </th>

              <th className="w-[15%] px-3 py-3">
                Assigned Agent
              </th>

              <th className="w-[11%] px-3 py-3">
                To Be Called
              </th>

              <th className="w-[13%] px-3 py-3">
                Campaign
              </th>

              <th className="w-[13%] px-3 py-3">
                Phone No
              </th>

              <th className="w-[8%] px-3 py-3">
                DNC
              </th>

              <th className="w-[10%] px-3 py-3">
                Status
              </th>

              <th className="w-[10%] px-3 py-3">
                Actions
              </th>

              {selectionMode && (
                <th className="w-[5%] px-3 py-3">
                  <input
                    type="checkbox"
                    checked={isSelectAllChecked}
                    onChange={handleSelectAll}
                    aria-label="Select all customers on the current page"
                    className="h-4 w-4 cursor-pointer"
                  />
                </th>
              )}
            </tr>
          </thead>

          <tbody className="text-center">
            {customerList.map(
              (customer, index) => {
                const isCalled = customer.callStatus?.toLowerCase() === "called";
                const disableSelection = selectionMode === "delete" && isCalled;
                const isRowSelected = Boolean(selectedCustomersMap[customer.id]);

                return (
                  <tr
                    key={customer.id}
                    className={`border-b hover:bg-gray-50 ${
                      isRowSelected
                        ? "bg-blue-50"
                        : "bg-white"
                    }`}
                  >
                    <td className="truncate px-3 py-4">
                      {index + 1 + (pagination.current_page - 1) * pagination.page_size}
                    </td>

                    <td
                      className="truncate px-3 py-4"
                      title={
                        customer.customer_name || "-"
                      }
                    >
                      {customer.customer_name || "-"}
                    </td>

                    <td
                      className="truncate px-3 py-4"
                      title={
                        customer.assigned_agent || "-"
                      }
                    >
                      {customer.assigned_agent || "-"}
                    </td>

                    <td className="truncate px-3 py-4">
                      {customer.tobe_called ?? 0}
                    </td>

                    <td
                      className="truncate px-3 py-4"
                      title={
                        customer.campaign_name || "-"
                      }
                    >
                      {customer.campaign_name || "-"}
                    </td>

                    <td
                      className="truncate px-3 py-4"
                      title={
                        customer.customer_phone_number || "-"
                      }
                    >
                      {customer.customer_phone_number || "-"}
                    </td>

                    <td className="px-3 py-4">
                      <span
                        className={`inline-flex items-center justify-center rounded-full border px-3 py-1 text-xs font-semibold ${
                          customer.dnc
                            ? "border-red-200 bg-red-100 text-red-700"
                            : "border-green-200 bg-green-100 text-green-700"
                        }`}
                      >
                        {customer.dnc ? "Yes" : "No"}
                      </span>
                    </td>

                    <td
                      className="truncate px-3 py-4"
                      title={
                        customer.callStatus || "-"
                      }
                    >
                      {customer.callStatus || "-"}
                    </td>

                    {/* Original icons kept unchanged */}
                    <td className="px-3 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <div
                          className="flex cursor-pointer rounded-md p-2 text-gray-300 hover:bg-light-white"
                          onClick={() =>
                            openEditModal(
                              customer
                            )
                          }
                          role="button"
                          tabIndex={0}
                          title="Edit customer"
                          onKeyDown={(event) => {
                            if (
                              event.key ===
                                "Enter" ||
                              event.key === " "
                            ) {
                              openEditModal(
                                customer
                              );
                            }
                          }}
                        >
                          <Edit
                            className={`width="26" height="26" fill="blue"`}
                            viewBox="0 0 26 26"
                            fill="green"
                            cx="8.5"
                            cy="8.5"
                            r="6"
                            fillRule="evenodd"
                            fillOpacity=".8"
                          />
                        </div>

                        <div
                          onClick={() => {
                            if (isCalled) {
                              Swal.fire({
                                icon: "error",
                                title:
                                  "Cannot Delete",
                                text: "Called entries cannot be deleted.",
                              });

                              return;
                            }

                            deleteCustomers([
                              customer,
                            ]);
                          }}
                          className="flex cursor-pointer rounded-md p-2 text-gray-300 hover:bg-light-white"
                          role="button"
                          tabIndex={0}
                          title="Delete customer"
                          onKeyDown={(event) => {
                            if (
                              event.key !==
                                "Enter" &&
                              event.key !== " "
                            ) {
                              return;
                            }

                            if (isCalled) {
                              Swal.fire({
                                icon: "error",
                                title:
                                  "Cannot Delete",
                                text: "Called entries cannot be deleted.",
                              });

                              return;
                            }

                            deleteCustomers([
                              customer,
                            ]);
                          }}
                        >
                          <Delete
                            className={`width="26" height="26" fill="blue"`}
                            viewBox="0 0 26 26"
                            fill="red"
                            cx="8.5"
                            cy="8.5"
                            r="6"
                            fillRule="evenodd"
                            fillOpacity=".8"
                          />
                        </div>
                      </div>
                    </td>

                    {selectionMode && (
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={isRowSelected}
                          disabled={
                            disableSelection
                          }
                          onChange={() =>
                            handleSelectRow(
                              customer
                            )
                          }
                          aria-label={`Select ${
                            customer.customer_name ||
                            "customer"
                          }`}
                          className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        />
                      </td>
                    )}
                  </tr>
                );
              }
            )}

            {!loading &&
              customerList.length === 0 && (
                <tr>
                  <td
                    colSpan={
                      selectionMode ? 10 : 9
                    }
                    className="px-4 py-10 text-center text-gray-500"
                  >
                    No customers found.
                  </td>
                </tr>
              )}

            {loading && (
              <tr>
                <td
                  colSpan={
                    selectionMode ? 10 : 9
                  }
                  className="px-4 py-10 text-center text-gray-500"
                >
                  Loading customers...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit customer modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl sm:p-6">
            <h2 className="mb-4 text-xl font-bold">
              Edit Customer
            </h2>

            <div className="mb-4">
              <label
                htmlFor="customer-name"
                className="block text-sm font-medium text-gray-700"
              >
                Name
              </label>

              <input
                id="customer-name"
                type="text"
                value={customerName}
                onChange={(event) =>
                  setCustomerName(
                    event.target.value
                  )
                }
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="customer-phone-number"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>

              <input
                id="customer-phone-number"
                type="text"
                value={customerPhoneNumber}
                maxLength={10}
                onChange={(event) =>
                  setCustomerPhoneNumber(
                    event.target.value.replace(/\D/g, "")
                  )
                }
                className="mt-1 block w-full rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
<div className="mb-4">
  {/* <label
    htmlFor="customer-dnc"
    className="mb-2 block text-sm font-medium text-gray-700"
  >
    DNC
  </label> */}

  <label
    htmlFor="customer-dnc"
    className="flex w-fit cursor-pointer items-center gap-2"
  >
    <span className="text-sm text-gray-700">
      Mark as DNC
    </span>
    <input
      id="customer-dnc"
      type="checkbox"
      checked={customerDnc}
      onChange={(event) =>
        setCustomerDnc(event.target.checked)
      }
      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
    />

    
  </label>
</div>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={saving}
                onClick={closeEditModal}
                className="rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={saveCustomer}
                className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="p-4 sm:p-8">
        <Pagination
          pagination_data={pagination}
          PaginationControl={paginationControl}
        />
      </div>
    </>
  );
};

export default CampaignCustomers;